import clsx from "clsx";
import { useCallback, useRef } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "common/components/ui/Accordion";
import RadioButton from "common/components/ui/RadioButton";
import { Tag } from "common/components/ui/Tag";
import {
  USER_PERMISSION_LEVELS,
  USER_PERMISSIONS_CATEGORIES,
} from "common/constants";
import { typedObjectEntries } from "common/helpers/utils";
import useMemberPermissionsState from "common/hooks/useMemberPermissionsState";
import type { UserPermissions } from "common/types";

export default function PermissionsSelection({
  permissions,
  setPermissions,
}: {
  permissions: UserPermissions;
  setPermissions: (updatedPermissions: UserPermissions) => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { memberPermissionsState } = useMemberPermissionsState(permissions);

  const scrollToOpenedItem = useCallback((itemId: string) => {
    //the accordion item
    const itemEl = document.getElementById(itemId);
    //the accordion content (animatable part)
    const contentEl = document.getElementById(`content-${itemId}`);

    if (!itemEl || !contentEl || !scrollContainerRef.current) return;

    const onAnimationEnd = () => {
      contentEl.removeEventListener("animationend", onAnimationEnd);

      const scrollContainer = scrollContainerRef.current;
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = itemEl.getBoundingClientRect();

      const overflowTop = itemRect.top - containerRect.top;
      const overflowBottom = itemRect.bottom - containerRect.bottom;

      // Clamp: only scroll if there's actual overflow in either direction
      const scrollOffset =
        Math.min(overflowTop, 0) || Math.max(overflowBottom, 0);

      if (scrollOffset) {
        scrollContainer.scrollBy({ top: scrollOffset, behavior: "smooth" });
      }
    };
    contentEl.addEventListener("animationend", onAnimationEnd);
  }, []);

  //some permissions have empty permission options, these are used for labels only
  const visiblePermissions = typedObjectEntries(
    USER_PERMISSIONS_CATEGORIES,
  ).filter(([, { permissionOptions }]) => permissionOptions.length);

  return (
    <>
      <button
        type="button"
        className="mb-3 ml-2.5 mr-auto text-button-sm text-purple-600"
        onClick={() => {
          setPermissions(
            visiblePermissions.map(([key]) => `${key}_full`) as UserPermissions,
          );
        }}
      >
        Set all permissions
      </button>
      <Accordion
        type="single"
        collapsible
        className="flex max-h-80 flex-col gap-y-3 overflow-y-auto scrollbar-thin"
        ref={scrollContainerRef}
        onValueChange={(openId) => scrollToOpenedItem(openId)}
      >
        {visiblePermissions.map(
          ([key, { label, permissionOptions, icon: Icon }]) => {
            const selectedPermissionLevel =
              USER_PERMISSION_LEVELS[memberPermissionsState[key]];
            return (
              <AccordionItem
                id={key}
                value={key}
                className="rounded-2xl border border-black-200"
                key={key}
              >
                <AccordionTrigger className="px-3.5 py-3 text-sm-bold gap-x-1.5">
                  <Icon
                    size={20}
                    className={clsx(key === "campaigns" && "rotate-90")}
                  />
                  {label}
                  <Tag
                    variant={selectedPermissionLevel.tagVariant}
                    className="ml-auto"
                  >
                    {selectedPermissionLevel.label}
                  </Tag>
                </AccordionTrigger>
                <AccordionContent
                  id={`content-${key}`}
                  className="flex flex-col gap-y-2 border-t border-t-black-200 p-3"
                >
                  {permissionOptions.map(({ description, level }) => {
                    const isSelected = memberPermissionsState[key] === level;

                    return (
                      <div className="flex items-start" key={level}>
                        <RadioButton
                          variant="purple"
                          id={`${key}_${level}`}
                          size="sm"
                          checked={isSelected}
                          onChange={() => {
                            const updatedPermissions = permissions.filter(
                              (permission) => !permission.startsWith(key),
                            );
                            if (level !== "none") {
                              updatedPermissions.push(
                                `${key}_${USER_PERMISSION_LEVELS[level].permissionName}`,
                              );
                            }
                            setPermissions(updatedPermissions);
                          }}
                        />
                        <label
                          className="flex flex-col pl-1.5"
                          htmlFor={`${key}_${level}`}
                        >
                          <span className="text-left text-xs-bold">
                            {USER_PERMISSION_LEVELS[level].label}
                          </span>
                          <span className="text-left text-button-xxs text-black-500">
                            {description}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>
            );
          },
        )}
      </Accordion>
    </>
  );
}
